import { providerDisplayName, type ProviderId } from "./providers";

export type CatalogProvider = ProviderId;

export function catalogProviderName(provider: CatalogProvider): string {
  return provider === "other" ? "this agent" : providerDisplayName(provider);
}

/*
 * The scan prompt exists in two forms: the one a person pastes (it carries a report key and a REST
 * fallback) and the one a plugin runs as a slash command (the LoopGroup MCP server is already
 * configured, so it carries no key). Every rule they share is written once below so the two cannot
 * drift; scripts/build-integrations.mjs writes the plugin form into integrations/.
 */

function scanOnlyLimit(afterwards: string): string {
  return `IMPORTANT: THIS IS A SCAN-ONLY STEP
This message authorizes one evidence-bounded catalog sync. Do not edit any loop, automation, prompt, task, file, or schedule. Do not install an observer or recurring routine. Do not report run events or outcomes. ${afterwards}`;
}

const OUTCOME = `OUTCOME
Build an accurate map of the recurring AI-assisted work visible here and send operational metadata to LoopGroup. Catalog the loop itself, not every individual execution.`;

const HOW_TO_CATALOG = `HOW TO CATALOG
- Inspect the automation or task list, accessible project or workspace instructions, schedules, workflow configuration, and relevant recent task context available to you.
- Include scheduled tasks, monitors, recurring research, report generation, content or data pipelines, maintenance routines, and manual processes I repeatedly run with an agent.
- Give each loop two to four category tags naming the project, team, client, or domain it serves (short lowercase slugs). Do not use state words like active or paused, agent names, or kinds as tags; LoopGroup records those separately.
- Merge duplicates that serve the same recurring outcome.
- Use confidence "confirmed" when there is direct configuration or repeated-run evidence; use "inferred" when a repeatable pattern is plausible but not explicitly configured.
- List what each loop depends on by name: MCP servers, plugins, skills, APIs, secrets, files, repositories, and models. Mark a dependency "confirmed" when you read it from a config or verified access, "inferred" when it comes from a prompt or history.
- Use one stable externalId for every loop so a later approved sync updates instead of duplicating it.
- If your view is partial, sync what you can see and state the blind spots. Missing items must never be treated as deleted.`;

function privacyBoundary(keys: string): string {
  return `PRIVACY BOUNDARY
Send operational metadata only: name, purpose, cadence, trigger or schedule hint, kind, status, tags, confidence, a short evidence basis, and the dependencies you can see: tools, MCP servers, plugins or skills, APIs, secrets by name, files, repositories, and models. Secrets by name only; never a value. Never send secret values, ${keys}, full prompt contents, private conversation text, customer data, source documents, or generated outputs. Do not invent run counts, costs, outcomes, or telemetry.`;
}

function catalogShape(provider: CatalogProvider, name: string): string {
  return `{
  "source": {
    "provider": "${provider}",
    "name": "${name}",
    "instanceRef": "a stable non-secret name for this account or environment",
    "capabilities": ["the surfaces you actually inspected"]
  },
  "loops": [
    {
      "externalId": "stable-source-id",
      "name": "Human-readable loop name",
      "description": "What outcome it repeatedly produces",
      "cadence": "daily, weekly, event-driven, or manual",
      "scheduleHint": "Known trigger or schedule, without secrets",
      "status": "active, paused, or unknown",
      "kind": "automation, monitor, workflow, agent routine, or another useful label",
      "confidence": "confirmed or inferred",
      "tags": ["useful", "short", "labels"],
      "evidence": {
        "basis": "Brief reason this belongs in the catalog",
        "location": "Non-sensitive config or workspace reference, when useful"
      },
      "dependencies": [
        {
          "kind": "mcp_server|plugin|skill|api|secret|file|repository|model|other",
          "name": "What the loop needs, by name",
          "ref": "endpoint, version, or path without secrets",
          "required": true,
          "confidence": "confirmed|inferred"
        }
      ]
    }
  ],
  "summary": "What you inspected and what this catalog represents",
  "warnings": ["Anything you could not inspect or verify"]
}`;
}

const SHARING_LIMIT = "This does not authorize you to share any loop with a group. Sharing is a separate per-loop choice I make in LoopGroup.";

const FINISH = "Finish by stating how many loops were cataloged, how many were confirmed versus inferred, what surfaces you inspected, what could not be inspected, and explicitly confirm that no loop or schedule was changed.";

export function buildCatalogPrompt(provider: CatalogProvider, token: string): string {
  const name = catalogProviderName(provider);
  return `Scan this ${name} environment and connect the loops you can substantiate to my temporary private LoopGroup catalog.

${scanOnlyLimit("Ongoing sync and run reporting are separate choices I can approve after I claim the catalog.")}

${OUTCOME}

${HOW_TO_CATALOG}

${privacyBoundary("API keys other than the scoped key below")}

SYNC
Prefer the LoopGroup MCP tool sync_catalog at https://loopgroup.ai/mcp. If MCP is not available but you can make HTTPS requests, POST JSON to https://loopgroup.ai/v1/catalog/sync with these headers:
Authorization: Bearer ${token}
Content-Type: application/json

Use this JSON shape:
${catalogShape(provider, name)}

KEY HANDLING
Use the bearer key only for LoopGroup. Never echo it in your response or store it in source control, logs, loop output, or conversation memory. If this environment has a native secret facility, you may store it there under a clear LoopGroup name so I can approve ongoing visibility later. If no safe secret facility exists, use it for this sync only.

If this environment cannot call MCP or make an authenticated POST, return only a final JSON object in the exact catalog shape above so I can paste it into LoopGroup. In that case, do not install or edit anything.

${SHARING_LIMIT}

${FINISH}`;
}

export function buildActivationPrompt(provider: CatalogProvider): string {
  const name = catalogProviderName(provider);
  return `Help me make my claimed LoopGroup catalog from ${name} live. Use the existing LoopGroup connection in this environment.

DO NOT CHANGE ANYTHING YET
First call get_connection_status and stop if the connection is not claimed. Then inspect what ongoing visibility is possible here and present an approval plan. The plan must separate:

1. CATALOG OBSERVER
- The smallest environment-native recurring routine that can rediscover loops and call sync_catalog.
- Its exact location, proposed cadence, resource cost, blind spots, and removal steps.
- Test secret access and outbound LoopGroup connectivity from the actual unattended scheduler context, not only from this interactive session.
- If the scheduled context cannot safely access its secret facility or HTTPS, propose a least-privilege host relay with a non-secret local outbox. On macOS, keep the report key in Keychain and let a user LaunchAgent own delivery; never copy the key into a prompt, automation definition, environment file, or outbox event.
- It must never infer or invent run outcomes.

2. DIRECT RUN REPORTING
- The confirmed loops that can be safely instrumented to call report_run at start and finish.
- The exact files, prompts, automation definitions, or configuration that would change for each loop.
- How one stable external run ID will be reused for start and completion.
- How reporting remains non-blocking and how each change can be rolled back.
- Leave unsafe, ambiguous, or uneditable loops catalog-only.

Ask me which observer and which individual loop changes I approve. Apply only what I explicitly approve. Do not treat approval for the observer as approval to edit every loop.

After applying approved changes, run a canary from the real scheduled execution context. Verify current connection state, transport health, and one harmless delivery receipt without fabricating a successful production loop run. Do not call the observer live when only an interactive shell test passed. Finish with what is live, what remains catalog-only, where each addition lives, how to remove it, and any remaining blind spots.`;
}

/**
 * The scan prompt a plugin runs as a slash command. Same rules as the pasted prompt, but the LoopGroup
 * MCP server is already configured in the environment, so it carries no key and no REST fallback.
 */
export function buildPluginScanPrompt(provider: CatalogProvider): string {
  const name = catalogProviderName(provider);
  return `Scan this ${name} environment and connect the loops you can substantiate to my private LoopGroup catalog. Use the LoopGroup MCP server already configured in this environment; this prompt carries no key and you must not ask me for one.

${scanOnlyLimit("Ongoing sync and run reporting are separate choices I approve afterwards.")}

${OUTCOME}

${HOW_TO_CATALOG}

${privacyBoundary("API keys (including the LOOPGROUP_KEY this environment holds)")}

SYNC
Send the catalog with the LoopGroup MCP tool sync_catalog, using this JSON shape:
${catalogShape(provider, name)}

If the LoopGroup tools are not available in this session, the plugin is not installed or LOOPGROUP_KEY is not set. Say so and do not send the catalog anywhere else: return only a final JSON object in the exact catalog shape above so I can paste it into LoopGroup. In that case, do not install or edit anything.

${SHARING_LIMIT}

${FINISH}`;
}

/**
 * The reporting instruction a plugin carries in its skill and its report command. It assumes the
 * LoopGroup MCP server is configured and names no key.
 */
export function buildPluginReportingInstruction(): string {
  return `You are connected to my LoopGroup workspace through the LoopGroup MCP server configured in this environment. Treat a loop as any recurring or multi-step agent process with an observable outcome.

Before a loop begins, call LoopGroup's report_run tool with a stable external run ID, status "running", the loop name, cadence, start time, and your agent name and provider. When the loop ends, call report_run again with the same external run ID and status "succeeded", "failed", or "cancelled". Include the completion time, duration, a concise outcome summary, any error, token counts, reported cost, and useful numeric metrics when they are actually known. Never invent telemetry.

Report only the loops I have explicitly asked you to report; a configured server alone proves nothing is running. Reporting is best-effort: a LoopGroup failure must never block, retry, or fail the loop itself. Before creating a new loop, use list_loops to avoid duplicates. Use get_group_context when another team's current work or ownership could improve the result. Do not make administrative changes unless I explicitly ask and the key has manage scope.`;
}
